import * as React from 'react';
import Select from 'react-select';
import * as helpers from '../../../../Partials/Shared/react-select-helpers/react-select.helpers';
import HttpService from '../../../../../services/rest/HttpService';
import { Label, FormGroup, Col, Row } from 'reactstrap';
import { remapEntitiesFromResponse } from '../../../translations/helpers/translations.helpers';
import { customOption } from '../../../../Partials/Blocky/partials/shared/custom-select-option';
import { SportTypes } from '../../../../../constants/sport.types';
import '../styles/country.style.scss';
import { useTranslation } from 'react-i18next';
import { CountryOption, CountryProps, TennisCountriesResponse, FootballCountriesResponse, TennisCountry } from '../types/country.types';

const CountrySelect: React.FunctionComponent<CountryProps> = ({
	onChange,
	value,
	isClearable,
	searchLanguage,
	colWidth,
	sportType = SportTypes.FOOTBALL,
}) => {
	const { t } = useTranslation();
	const [options, setOptions] = React.useState<CountryOption[]>([]);
	const [selectedOption, setSelectedOption] = React.useState<CountryOption | null>(null);

	const onCountrySelect = (selection: CountryOption | null) => {
		setSelectedOption(selection);

		if (selection) {
			onChange(helpers.optionToItem(selection));
		} else {
			onChange(null);
		}
	};

	React.useEffect(() => {
		if (sportType === SportTypes.TENNIS) {
			HttpService.getTennisCountries().then((response: TennisCountriesResponse) => {
				if (response && response.data && response.data.data) {
					const countriesData = response.data.data;
					const countryOptions: CountryOption[] = countriesData.map((country: TennisCountry) => {
						let flagUrl: string | undefined = undefined;
						if (country.assets && country.assets.flag) {
							flagUrl = country.assets.flag.url;
						}
						return {
							value: country.id,
							label: country.name,
							icon: flagUrl,
							data: {
								id: country.id,
								title: country.name,
								icon: flagUrl,
								countryIcon: flagUrl,
								flag: flagUrl,
							},
						};
					});
					setOptions(countryOptions);
					if (value && value.id) {
						const selectedCountry = countryOptions.find((option: CountryOption) => option.value === value.id);
						if (selectedCountry) {
							setSelectedOption(selectedCountry);
						}
					}
				}
			});
		} else {
			const headers = {
				'Accept-Language': searchLanguage ? searchLanguage.id : 'en',
			};
			HttpService.instanceFootball(searchLanguage ? searchLanguage.id : null)
				.get('/countries', { headers })
				.then((response: { data: FootballCountriesResponse['data'] }) => {
					const mappedOptions = helpers.itemsToOptions(remapEntitiesFromResponse(response.data, 'country'), false, t, 'country');
					if (Array.isArray(mappedOptions)) {
						const countryOptions = mappedOptions as CountryOption[];
						setOptions(countryOptions);
						if (value && value.id) {
							const selectedCountry = countryOptions.find((option: CountryOption) => option.value === value.id);
							if (selectedCountry) {
								setSelectedOption(selectedCountry);
							}
						}
					} else {
						setOptions([]);
					}
				});
		}
	}, [t, searchLanguage, value, sportType]);

	const formatTennisOptionLabel = (option: CountryOption) => {
		const flagUrl = option.data.flag;

		return (
			<div className='country-select-option'>
				{flagUrl ? (
					<img
						src={flagUrl}
						alt={`${option.label} flag`}
						className='country-select-flag'
						onError={(e) => {
							(e.target as HTMLImageElement).style.display = 'none';
							const placeholder = (e.target as HTMLImageElement).nextElementSibling;
							if (placeholder) {
								(placeholder as HTMLElement).style.display = 'flex';
							}
						}}
					/>
				) : null}
				<div className='country-select-flag-placeholder' style={{ display: flagUrl ? 'none' : 'flex' }}>
					<span>{option.label.substring(0, 2).toUpperCase()}</span>
				</div>
				<span>{option.label}</span>
			</div>
		);
	};

	const noOptionsMessage = (inputValue: { inputValue: string }) => {
		return inputValue.inputValue ? t('no_options') : null;
	};

	return (
		<Row>
			<Col xs={colWidth ? colWidth : '6'}>
				<FormGroup>
					<Label htmlFor='manual-data-entry-country'>{t('country')}</Label>
					<Select
						inputId='manual-data-entry-country'
						value={selectedOption}
						options={options}
						onChange={onCountrySelect}
						placeholder={t('select_country')}
						formatOptionLabel={sportType === SportTypes.TENNIS ? formatTennisOptionLabel : customOption}
						noOptionsMessage={noOptionsMessage}
						isClearable={isClearable}
					/>
				</FormGroup>
			</Col>
		</Row>
	);
};

export default CountrySelect;
